import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useDialogFocus, useScrollLock } from '../lib/hooks';
import { cx } from './cx';
import { Cutout, Plate } from './components/Photo';
import { DesktopDownload } from './components/DesktopDownload';
import { Marquee } from './components/Marquee';
import { ArrowRight, CloseIcon, InstagramIcon, MenuIcon, TikTokIcon, XIcon } from './components/icons';
import ui from './ui.module.css';
import s from './MarketingLayout.module.css';

const NAV = [
  { to: '/features', label: 'Features', index: '01' },
  { to: '/creators', label: 'Creators', index: '02' },
  { to: '/pricing', label: 'Pricing', index: '03' },
  { to: '/manifesto', label: 'Manifesto', index: '04' },
] as const;

interface FooterLink {
  label: string;
  to?: string;
  href?: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

const FOOTER_COLUMNS: FooterColumn[] = [
  {
    title: 'Product',
    links: [
      { label: 'Features', to: '/features' },
      { label: 'For creators', to: '/creators' },
      { label: 'Pricing', to: '/pricing' },
      // The festival issue. Linked from the footer rather than the primary nav:
      // it is a hackathon entry, not a fifth pillar of the product.
      { label: 'Outside Lands', to: '/outsidelands' },
      { label: 'Open the studio', to: '/auth' },
    ],
  },
  {
    title: 'Masthead',
    links: [
      { label: 'Manifesto', to: '/manifesto' },
      { label: 'Press', href: 'mailto:press@racksavant.com' },
      { label: 'Working here', href: 'mailto:jobs@racksavant.com' },
      { label: 'Say hello', href: 'mailto:hello@racksavant.com' },
    ],
  },
  {
    title: 'Small print',
    links: [
      { label: 'Privacy', to: '/privacy' },
      { label: 'Terms', to: '/terms' },
      { label: 'The rails', to: '/#rails' },
      { label: 'Delete my data', href: 'mailto:privacy@racksavant.com?subject=Delete%20my%20data' },
    ],
  },
];

const SOCIALS = [
  { label: 'RackSavant on Instagram', href: 'https://instagram.com/racksavant', Icon: InstagramIcon },
  { label: 'RackSavant on TikTok', href: 'https://tiktok.com/@racksavant', Icon: TikTokIcon },
  { label: 'RackSavant on X', href: 'https://x.com/racksavant', Icon: XIcon },
] as const;

/**
 * The masthead. Typographic — the old gradient-lens logotype was the single
 * most SaaS object on the site. A flash registration mark, then the name in
 * the display serif with the second half in italic.
 */
function Wordmark({ onClick }: { onClick?: () => void }) {
  return (
    <Link to="/" className={s.brand} onClick={onClick} aria-label="RackSavant — home">
      <span className={s.brandMark} aria-hidden="true" />
      <span className={s.brandName}>
        Rack<em>savant</em>
      </span>
    </Link>
  );
}

export function MarketingLayout() {
  const location = useLocation();
  const [condensed, setCondensed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const burgerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const onScroll = () => setCondensed(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Scroll to the top on route change, but honour in-page anchors — react-router
  // does not restore hash targets on its own.
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return;
    }
    const target = document.getElementById(location.hash.slice(1));
    if (target) target.scrollIntoView({ block: 'start' });
    else window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  // Lock the page behind the full-screen menu. Focus in, Tab contained, Escape
  // out, focus back on the burger — the header and <main> stay in the DOM and
  // tabbable, so without the trap Tab walked straight out of an `aria-modal`
  // dialog into content assistive tech has been told is not there.
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useScrollLock(menuOpen);
  useDialogFocus(menuOpen, menuRef, closeMenu);

  return (
    <div className={ui.root}>
      <a className={ui.skipLink} href="#main">
        Skip to content
      </a>

      <header className={s.header} data-condensed={condensed ? 'true' : 'false'}>
        <div className={cx(ui.container, s.headerInner)}>
          <Wordmark />

          <nav className={s.nav} aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => cx(s.navLink, isActive && s.navLinkActive)}
              >
                <span className={s.navIndex} aria-hidden="true">
                  {item.index}
                </span>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className={s.headerActions}>
            <Link to="/auth" className={s.signIn}>
              Sign in
            </Link>
            <Link to="/auth" className={cx(ui.btn, ui.primary, ui.btnSm, s.headerCta)}>
              Open the studio
            </Link>
            <button
              ref={burgerRef}
              type="button"
              className={s.burger}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <MenuIcon size={22} />
            </button>
          </div>
        </div>
      </header>

      {menuOpen ? (
        <div
          ref={menuRef}
          tabIndex={-1}
          className={s.menu}
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
        >
          <div className={cx(ui.container, s.menuTop)}>
            <Wordmark onClick={() => setMenuOpen(false)} />
            <button
              type="button"
              className={cx(s.burger, s.burgerOpen)}
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
            >
              <CloseIcon size={22} />
            </button>
          </div>

          <nav className={cx(ui.container, s.menuBody)} aria-label="Mobile">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => cx(s.menuLink, isActive && s.menuLinkActive)}
              >
                <span aria-hidden="true">{item.index}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className={cx(ui.container, s.menuFoot)}>
            <div className={s.menuActions}>
              <Link to="/auth" className={cx(ui.btn, ui.primary)}>
                Open the studio
                <ArrowRight size={16} />
              </Link>
              <Link to="/auth" className={cx(ui.btn, ui.ghost)}>
                Sign in
              </Link>
            </div>
            <Plate id="detail" className={s.menuPlate} caption={false} ratio={16 / 9} />
          </div>
        </div>
      ) : null}

      <main id="main">
        <Outlet />
      </main>

      <footer className={s.footer}>
        <Marquee duration={34000} className={s.footMarquee}>
          {[0, 1, 2].map((n) => (
            <span key={n} className={s.footMarqueeItem}>
              Try on the feeling
              <span className={s.footMarqueeStar} aria-hidden="true" />
            </span>
          ))}
        </Marquee>

        <div className={cx(ui.container, s.footInner)}>
          <div className={s.footBrand}>
            <Wordmark />
            <p className={s.footNote}>
              A mirror that never edits you. We do not alter your body, we do not comment on it, and
              we do not train on your photographs. Delete means delete.
            </p>
            <div className={s.socials}>
              {SOCIALS.map(({ label, href, Icon }) => (
                <a
                  key={href}
                  className={s.social}
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
            <DesktopDownload variant="footer" className={s.footDownload} />
            <Cutout id="boots" width={168} tilt={-4} className={s.footCutout} />
          </div>

          {FOOTER_COLUMNS.map((column) => (
            <div key={column.title} className={s.footCol}>
              <h2 className={s.colTitle}>{column.title}</h2>
              <ul className={s.colList}>
                {column.links.map((link) => (
                  <li key={link.label}>
                    {link.to ? (
                      <Link to={link.to}>{link.label}</Link>
                    ) : (
                      <a href={link.href}>{link.label}</a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className={cx(ui.container, s.footBottom)}>
          <p>© 2026 RackSavant Labs, Inc.</p>
          <p>Made for people who get looked at.</p>
          <p className={s.colophon}>Set in Instrument Serif and Inter.</p>
        </div>
      </footer>
    </div>
  );
}
